/**
 * The headless half of replay playback — the third of "one sim, three jobs"
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md): re-running a stored {@link RunRecord}
 * through the SAME deterministic core that the live game and the server's
 * anti-cheat re-simulation use, so a replay reproduces the original run
 * byte-for-byte.
 *
 * This class owns the deterministic side only — it builds a {@link WorldState}
 * from the record and advances it one fixed step at a time, resolving each
 * step's {@link PlayerInput} from `record.samples`. It holds NO Phaser handles;
 * {@link ReplayScene} owns the rendering. Splitting it out keeps the stepping
 * logic engine-agnostic and trivially unit-testable, and keeps the scene thin.
 *
 * CRITICAL — must match {@link import('../core/world').runSimulation} exactly:
 *  - input is sampled at the START of each step (`world.elapsedMs`), by linear
 *    interpolation between the bracketing samples, holding the nearest sample
 *    before the first / after the last (no extrapolation);
 *  - the replay ends when the player dies OR `elapsedMs` reaches the last
 *    sample's `tMs` (the recorded run length).
 * `core/world.ts`'s `inputAt` is module-private, so the identical logic is
 * mirrored here as {@link resolveInput}; keep the two in lockstep if either
 * changes (the byte-for-byte guarantee depends on it).
 */
import type { PlayerInput, RunInputSample, RunRecord, Viewport, WorldState } from '../core';
import { SIM, createWorld, stepWorld, scoreFromElapsedMs } from '../core';

/** Why a replay stopped — drives the outcome banner in {@link ReplayScene}. */
export type ReplayEndReason = 'death' | 'survived';

/** The authoritative outcome once a replay reaches its end. */
export interface ReplayOutcome {
  /** Survival score via {@link scoreFromElapsedMs} (matches the server's). */
  score: number;
  /** Total survival time the replay reproduced (ms). */
  durationMs: number;
  /** Which attack family landed the kill, or null if the run never ended in death. */
  deathFamily: WorldState['deathFamily'];
  /** `death` if the player was hit; `survived` if the input timeline simply ran out. */
  reason: ReplayEndReason;
}

/**
 * Resolve the player's pointer target at time `tMs` from the recorded samples by
 * linear interpolation — a verbatim mirror of `inputAt` in `core/world.ts` (which
 * is module-private there). Samples are sorted ascending by `tMs`; before the
 * first / after the last sample the nearest sample is held (no extrapolation); an
 * empty timeline reports the viewport centre so the ball sits still.
 *
 * Kept byte-identical to core so the replay reproduces the run exactly.
 */
function resolveInput(
  samples: readonly RunInputSample[],
  tMs: number,
  viewport: Viewport,
): PlayerInput {
  const n = samples.length;
  if (n === 0) return { x: viewport.width / 2, y: viewport.height / 2 };

  const first = samples[0]!;
  if (tMs <= first.tMs) return { x: first.x, y: first.y };

  const last = samples[n - 1]!;
  if (tMs >= last.tMs) return { x: last.x, y: last.y };

  // Linear scan for the bracketing pair (sample counts are tiny — a few KB).
  for (let i = 1; i < n; i++) {
    const b = samples[i]!;
    if (tMs <= b.tMs) {
      const a = samples[i - 1]!;
      const span = b.tMs - a.tMs;
      if (span <= 0) return { x: b.x, y: b.y };
      const f = (tMs - a.tMs) / span;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    }
  }
  return { x: last.x, y: last.y };
}

/**
 * Drives one replay: a {@link WorldState} rebuilt from a {@link RunRecord} plus
 * the cursor that resolves the recorded input at each fixed step. The scene polls
 * {@link advance} once per render frame (with the frame delta) and renders the
 * exposed {@link world}; restart simply rebuilds from the same record.
 */
export class ReplayDirector {
  readonly record: RunRecord;

  /** The live world being replayed — read by {@link ReplayScene} to render. */
  private worldState: WorldState;

  /** Recorded run length (ms): the last sample's timestamp, or 0 if empty. */
  private readonly endMs: number;

  /** True once the player died or the timeline ran out; advancing then no-ops. */
  private done = false;

  constructor(record: RunRecord) {
    this.record = record;
    this.endMs = record.samples.length > 0 ? record.samples[record.samples.length - 1]!.tMs : 0;
    this.worldState = this.buildWorld();
  }

  /**
   * Build a fresh world from the record AND seed the player at the first input sample
   * — a verbatim mirror of `runSimulation` (and the live client's first-step seed), so
   * all three jobs start at the SAME position. Without this the replay would ease in
   * from the viewport centre and drift from the validated run on the opening steps.
   */
  private buildWorld(): WorldState {
    const world = createWorld(this.record.seed, this.record.viewport, this.record.mode);
    if (this.record.samples.length > 0) {
      const start = resolveInput(this.record.samples, 0, this.record.viewport);
      world.playerX = start.x;
      world.playerY = start.y;
    }
    return world;
  }

  /** The world being replayed (for rendering). Mutated in place by {@link advance}. */
  get world(): WorldState {
    return this.worldState;
  }

  /** Has the replay reached its end (death or the timeline ran out)? */
  get finished(): boolean {
    return this.done;
  }

  /** Survival score at the current point in the replay (matches the server's). */
  get currentScore(): number {
    return scoreFromElapsedMs(this.worldState.elapsedMs);
  }

  /** Progress through the recorded timeline in [0,1] (1 once finished/empty). */
  get progress(): number {
    if (this.endMs <= 0) return 1;
    return Math.min(this.worldState.elapsedMs / this.endMs, 1);
  }

  /**
   * Advance the replay by exactly `steps` fixed {@link SIM.fixedDeltaMs} ticks,
   * mutating {@link world} in place. Each tick resolves the input at the step
   * boundary (`world.elapsedMs`) — identical to the server's re-simulation — and
   * stops early the moment the player dies or the recorded timeline is exhausted.
   *
   * @param steps how many fixed steps to run this frame (from the scene's clock).
   * @returns true if the replay finished during this call (so the scene can show
   *          the outcome exactly once).
   */
  advance(steps: number): boolean {
    if (this.done) return false;
    const dt = SIM.fixedDeltaMs;
    const samples = this.record.samples;
    const view = this.worldState.viewport;

    for (let i = 0; i < steps; i++) {
      if (!this.worldState.alive || this.worldState.elapsedMs >= this.endMs) {
        this.done = true;
        return true;
      }
      const input = resolveInput(samples, this.worldState.elapsedMs, view);
      stepWorld(this.worldState, dt, input);
    }

    // A hit inside this batch ends the replay immediately (matches runSimulation,
    // which stops the clock at the lethal step).
    if (!this.worldState.alive) {
      this.done = true;
      return true;
    }
    return false;
  }

  /**
   * The replay's final outcome. Meaningful once {@link finished} is true; before
   * that it reflects the current point of the replay.
   */
  outcome(): ReplayOutcome {
    return {
      score: scoreFromElapsedMs(this.worldState.elapsedMs),
      durationMs: this.worldState.elapsedMs,
      deathFamily: this.worldState.deathFamily,
      reason: this.worldState.alive ? 'survived' : 'death',
    };
  }

  /**
   * Rewind to the start: rebuild a fresh world from the same record (the seeded
   * RNG re-derives the identical hazard stream) and clear the finished flag.
   */
  restart(): void {
    this.worldState = this.buildWorld();
    this.done = false;
  }

  // NOTE: the simulated viewport is FIXED at `record.viewport` and must never change
  // mid-playback (it drives core's clamp + hazard geometry, so mutating it would
  // diverge the replay from the authoritative run). A live resize only re-fits the
  // CAMERA (see ReplayScene.onResize → fitCameraToField), never the world.
}
